import { defineComponent, ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { db, auth } from '@/firebase';
import { collection, onSnapshot, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import AddUserPopup from '@/components/AddUserPopup.vue';
export default defineComponent({
    name: 'Clients',
    components: { AddUserPopup },
    setup() {
        const router = useRouter();
        const users = ref([]);
        const loading = ref(true);
        const openMenu = ref(null);
        const placeholder = '/avatar-placeholder.png';
        const roleFilter = ref('All');
        const searchQuery = ref('');
        const showAddUser = ref(false);
        const addUserInitial = ref(null);
        // view / edit modal state
        const showViewModal = ref(false);
        const showEditModal = ref(false);
        const selectedUser = ref(null);
        const editForm = ref({}); // simple mutable form object
        const savingEdit = ref(false);
        // pagination
        const page = ref(1);
        const perPage = ref(12);
        const dummyUsers = [];
        let unsubscribeRef = null;
        const fetchUsers = async () => {
            loading.value = true;
            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, orderBy('createdAt', 'desc'));
                const unsubscribe = onSnapshot(q, (snap) => {
                    users.value = snap.docs.map(d => {
                        const data = d.data();
                        return {
                            id: d.id,
                            displayName: data.displayName ?? data.name ?? data.fullName,
                            email: data.email,
                            role: (data.role ?? 'client').toString().toLowerCase(),
                            photoURL: data.photoURL ?? data.avatarUrl ?? '',
                            orders: data.ordersCount ?? 0,
                            monthTotal: data.monthTotal ?? 0,
                            companyName: data.companyName ?? ''
                        };
                    });
                    if (users.value.length === 0 && dummyUsers.length)
                        users.value = dummyUsers.slice();
                    loading.value = false;
                }, (err) => {
                    console.error('[Clients] onSnapshot error:', err);
                    users.value = dummyUsers.slice();
                    loading.value = false;
                });
                unsubscribeRef = unsubscribe;
            }
            catch (e) {
                console.error('[Clients] fetchUsers failed:', e?.code ?? e, e?.message ?? '');
                users.value = dummyUsers.slice();
                loading.value = false;
            }
        };
        onUnmounted(() => {
            if (unsubscribeRef)
                unsubscribeRef();
        });
        const onAddUser = () => { addUserInitial.value = null; showAddUser.value = true; };
        // handle update emitted by AddUserPopup (edit mode)
        const handleUpdate = async (payload) => {
            try {
                const { id, ...fields } = payload || {};
                if (!id)
                    return;
                const userDoc = doc(db, 'users', id);
                await updateDoc(userDoc, {
                    displayName: fields.displayName ?? '',
                    role: (fields.role ?? 'client').toString().toLowerCase(),
                    companyName: fields.companyName ?? '',
                    photoURL: fields.photoBase64 ? fields.photoBase64 : (fields.photoURL ?? '')
                });
                console.log('handleUpdate: user updated', id);
                // close popup if still open
                showAddUser.value = false;
            }
            catch (e) {
                console.error('handleUpdate failed', e);
                alert('Failed to save user edits. See console for details.');
            }
        };
        // handler for AddUserPopup 'created' event — receives uid or full object
        const onCreated = (payload) => {
            showAddUser.value = false;
            // payload may be uid string or object — if object, dedupe and push; otherwise let realtime listener add it
            if (!payload)
                return;
            if (typeof payload === 'string') {
                // let realtime listener supply record
                return;
            }
            // payload is object
            const existing = users.value.find(x => x.id === payload.id);
            if (existing)
                return;
            users.value.unshift({
                id: payload.id,
                displayName: payload.displayName,
                email: payload.email,
                role: (payload.role ?? 'client').toString().toLowerCase(),
                photoURL: payload.photoURL ?? '',
                orders: payload.orders ?? 0,
                monthTotal: payload.monthTotal ?? 0,
                companyName: payload.companyName ?? ''
            });
        };
        function createUser(u) {
            // backwards compatibility: some callers emit 'create' with full object
            onCreated(u);
        }
        const toggleMenu = (id) => {
            const key = String(id ?? '');
            openMenu.value = openMenu.value === key ? null : key;
        };
        const viewProfile = (u) => {
            openMenu.value = null;
            if (!u?.id)
                return;
            // navigate to the full-screen profile page
            router.push({ path: `/users/${u.id}` });
        };
        // open edit modal from profile view
        const openEditFromProfile = (u) => {
            if (!u)
                return;
            editForm.value = { ...u };
            showEditModal.value = true;
            showViewModal.value = false;
        };
        const closeView = () => {
            showViewModal.value = false;
            selectedUser.value = null;
        };
        const editUser = (u) => {
            editForm.value = { ...u }; // shallow copy into form
            showEditModal.value = true;
            openMenu.value = null;
        };
        const closeEdit = () => {
            showEditModal.value = false;
            editForm.value = {};
        };
        const saveEdit = async () => {
            if (!editForm.value?.id)
                return;
            savingEdit.value = true;
            try {
                const u = editForm.value;
                const userDoc = doc(db, 'users', u.id);
                await updateDoc(userDoc, {
                    displayName: u.displayName ?? '',
                    role: (u.role ?? 'client').toString().toLowerCase(),
                    companyName: u.companyName ?? '',
                    photoURL: u.photoURL ?? ''
                });
                console.log('User updated', u.id);
                closeEdit();
            }
            catch (e) {
                console.error('saveEdit failed', e);
                alert('Failed to save user. See console for details.');
            }
            finally {
                savingEdit.value = false;
            }
        };
        const passwordResetRequest = async (u) => {
            openMenu.value = null;
            if (!u.email) {
                alert('User has no email');
                return;
            }
            try {
                await sendPasswordResetEmail(auth, u.email);
                alert('Password reset email sent to ' + u.email);
            }
            catch (e) {
                console.error('sendPasswordResetEmail failed', e);
                alert('Failed to send password reset: ' + (e?.message ?? e));
            }
        };
        const formatMoney = (n) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
        const setFilter = (f) => { roleFilter.value = f; };
        const filteredUsers = computed(() => {
            let list = users.value.slice();
            if (roleFilter.value !== 'All') {
                list = list.filter(u => (u.role ?? 'client').toLowerCase() === roleFilter.value);
            }
            const q = (searchQuery.value || '').trim().toLowerCase();
            if (q) {
                list = list.filter(u => (u.displayName ?? '').toLowerCase().includes(q)
                    || (u.email ?? '').toLowerCase().includes(q)
                    || (u.role ?? '').toLowerCase().includes(q));
            }
            return list;
        });
        const total = computed(() => filteredUsers.value.length);
        const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)));
        const showStart = computed(() => total.value === 0 ? 0 : (page.value - 1) * perPage.value + 1);
        const showEnd = computed(() => Math.min(total.value, page.value * perPage.value));
        const pagedUsers = computed(() => {
            const start = (page.value - 1) * perPage.value;
            return filteredUsers.value.slice(start, start + perPage.value);
        });
        const pageRange = computed(() => {
            const pages = [];
            const maxButtons = 7;
            const tp = totalPages.value;
            if (tp <= maxButtons) {
                for (let i = 1; i <= tp; i++)
                    pages.push(i);
                return pages;
            }
            const left = Math.max(1, page.value - 2);
            const right = Math.min(tp, page.value + 2);
            if (left > 1) {
                pages.push(1);
                if (left > 2)
                    pages.push('...');
            }
            for (let i = left; i <= right; i++)
                pages.push(i);
            if (right < tp) {
                if (right < tp - 1)
                    pages.push('...');
                pages.push(tp);
            }
            return pages;
        });
        function goToPage(p) { if (p < 1)
            p = 1; if (p > totalPages.value)
            p = totalPages.value; page.value = p; window.scrollTo({ top: 0, behavior: 'smooth' }); }
        function prevPage() { if (page.value > 1)
            goToPage(page.value - 1); }
        function nextPage() { if (page.value < totalPages.value)
            goToPage(page.value + 1); }
        watch([roleFilter, searchQuery, users], () => { page.value = 1; });
        onMounted(() => { fetchUsers(); });
        const exportUsers = () => {
            const rows = filteredUsers.value.map(u => ({ id: u.id, name: u.displayName ?? '', email: u.email ?? '', role: u.role ?? '', orders: u.orders ?? 0, monthTotal: u.monthTotal ?? 0 }));
            const csv = [['id', 'name', 'email', 'role', 'orders', 'monthTotal'], ...rows.map(r => [r.id, r.name, r.email, r.role, r.orders, r.monthTotal])].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `users_export_${Date.now()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        };
        return {
            users, loading, onAddUser, placeholder, openMenu, toggleMenu, viewProfile, editUser,
            showAddUser, addUserInitial, createUser, onCreated,
            formatMoney, roleFilter, setFilter, filteredUsers, searchQuery, exportUsers,
            pagedUsers, total, page, perPage, totalPages, showStart, showEnd, pageRange,
            goToPage, prevPage, nextPage,
            // modals and edit handlers
            showViewModal, showEditModal, selectedUser, editForm, saveEdit, savingEdit, closeView, closeEdit, passwordResetRequest,
            openEditFromProfile,
            // handle update from AddUserPopup
            handleUpdate,
        };
    },
});
